/**
 * <EndSessionPrompt>
 *
 * §8 of <ZoomPanel> (`docs/ui-designs/06-telehealth-and-scribe.md`).
 *
 * Two states, both scoped to the parent Zoom panel via `absolute inset-0`:
 *
 *   1. Confirm — "End Session" headline + body + two vertical buttons.
 *   2. Generating — progress bar + "Generating Progress Note…" driven by the
 *      REAL pipeline stages the BE streams over NDJSON (reading conversation →
 *      composing → reviewing → finalizing; PD-1139). The bar eases toward the
 *      latest real pct and never fabricates progress beyond it. On completion
 *      the component unmounts (the note opens) — there is no snap to 100%.
 *
 * The parent <aside> is `position: fixed`, which establishes the containing
 * block; both states stay inside the right-rail panel and never dim the
 * surrounding chart. The doctor may be on any patient-chart sub-route when
 * they end a scribe call (Demographics, Labs, etc.) — darkening the whole
 * viewport on those routes hides unrelated chart content for no UX benefit.
 *
 * The orchestration still flows through `useFinalizeScribe` — this component
 * is purely presentational and the parent ZoomPanel wires the callbacks.
 */
import { useEffect, useRef, useState } from 'react'
import { IconCheck, IconLoader2 } from '@tabler/icons-react'
import { scribeText, SCRIBE_ACCENT } from './scribeText'

/** Per-tick fraction the bar closes toward the latest real pct (eased glide). */
const EASE_FACTOR = 0.15

/**
 * Same component, two surfaces: `dark` inside the rail's slate card, `light`
 * ("clear") on the white note editor. The light accents are one step deeper
 * (amber-600 / green-600) so they keep contrast on white.
 */
const VARIANT = {
  dark: {
    done: '#22c55e',
    active: '#fbbf24',
    track: 'bg-slate-800',
    doneText: 'text-slate-300',
    activeText: 'text-[#fbbf24]',
    pendingText: 'text-slate-500',
    pendingDot: 'border-slate-600',
  },
  light: {
    done: '#16a34a',
    active: '#d97706',
    track: 'bg-slate-200',
    doneText: 'text-slate-600',
    activeText: 'text-[#d97706]',
    pendingText: 'text-slate-400',
    pendingDot: 'border-slate-300',
  },
} as const

type GeneratingVariant = keyof typeof VARIANT

/**
 * The BE's four phases and the pct each opens at (`generate-draft.handler.ts`
 * PROGRESS_* constants). State comes from the latched pct, not the stage string,
 * so a phase can't un-tick when the pipeline loops back to regenerate.
 */
const STEPS = [
  { threshold: 8, label: 'Reading the conversation' },
  { threshold: 20, label: 'Composing the progress note' },
  { threshold: 62, label: 'Reviewing for accuracy' },
  { threshold: 92, label: 'Finalizing' },
] as const

/** The BE restarts its ramp here for each extra verification pass. */
const REVIEW_STEP = STEPS.find((step) => step.label.startsWith('Reviewing'))!

function StepRow({
  label,
  state,
  note,
  palette,
}: {
  label: string
  state: 'done' | 'active' | 'pending'
  /** Sub-line under the active row — explains an unusually long dwell. */
  note?: string
  palette: (typeof VARIANT)[GeneratingVariant]
}) {
  return (
    <li
      className="flex flex-wrap items-center gap-x-2 gap-y-[3px]"
      data-testid={`zoom-panel-end-prompt-step-${state}`}
      aria-current={state === 'active' ? 'step' : undefined}
    >
      <span className="flex h-[14px] w-[14px] shrink-0 items-center justify-center" aria-hidden>
        {state === 'done' ? (
          <IconCheck size={12} stroke={3} style={{ color: palette.done }} />
        ) : state === 'active' ? (
          <IconLoader2
            size={12}
            className="animate-spin motion-reduce:animate-none"
            style={{ color: palette.active }}
          />
        ) : (
          <span className={`h-[7px] w-[7px] rounded-full border ${palette.pendingDot}`} />
        )}
      </span>
      <span
        className={
          state === 'active'
            ? palette.activeText
            : state === 'done'
              ? palette.doneText
              : palette.pendingText
        }
      >
        {label}
      </span>
      {note ? (
        <span
          className="w-full pl-[22px] text-slate-500"
          data-testid="zoom-panel-end-prompt-step-note"
        >
          {note}
        </span>
      ) : null}
    </li>
  )
}

/**
 * Exported for reuse: the Progress Note editor's Regenerate overlay shows the
 * SAME generating card as the rail. Dark-theme styling — hosts must provide
 * the dark card container (see the render below / ProgressNoteEdit's overlay).
 */
export function GeneratingProgress({
  progress,
  draftReady,
  variant = 'dark',
}: {
  progress?: { stage: string; pct: number } | null
  draftReady?: boolean
  variant?: GeneratingVariant
}) {
  const palette = VARIANT[variant]
  const [displayed, setDisplayed] = useState(0)
  // Monotonic BE pct — never fabricate progress beyond it. `draftReady` is the
  // one exception: the note exists, so 100% is earned.
  const target = draftReady ? 100 : (progress?.pct ?? 0)
  const stage = progress?.stage ?? ''
  const rawPct = progress?.pct ?? 0
  // Every regeneration re-emits the SAME stage string, so a further pass is only
  // detectable from the BE ramp restarting at the review floor; a drop below it
  // is a whole new generation (retry). Without this the row parks 30-45s unexplained.
  const [extraPasses, setExtraPasses] = useState(0)
  // Highest pct the BE has reported. Steps key off this, not the eased bar value
  // — that lags a stage jump by up to 2.8s, long enough to render the live stage
  // label against the previous row.
  const [reachedPct, setReachedPct] = useState(0)
  const prevRawPct = useRef(0)
  const regenerating = stage.includes('regenerating')

  useEffect(() => {
    // Strictly-less-than is what makes a repeated (StrictMode) run safe.
    if (rawPct < prevRawPct.current) {
      setExtraPasses((prev) => (rawPct >= REVIEW_STEP.threshold ? prev + 1 : 0))
    }
    prevRawPct.current = rawPct
    setReachedPct((prev) => Math.max(prev, rawPct))
  }, [rawPct])

  useEffect(() => {
    const id = window.setInterval(() => {
      // Math.max(prev, …) is a monotonic guard — the bar never visually
      // regresses even if a lower pct ever arrives.
      setDisplayed((prev) => Math.max(prev, Math.min(target, prev + (target - prev) * EASE_FACTOR)))
    }, 100)
    return () => window.clearInterval(id)
  }, [target])

  const label = progress?.stage ?? 'Preparing…'
  // Round for display so the chip percentage doesn't flicker every tick.
  const pct = Math.round(displayed)
  // Accumulating checks are the proof of progress while the bar sits still
  // through the ~25-40s judge. A ready draft lands past the last step: all done.
  const activeIndex = draftReady
    ? STEPS.length
    : STEPS.reduce((last, step, index) => (reachedPct >= step.threshold ? index : last), 0)
  // Every amber surface turns green together — the sweep reads as "done".
  const accent = draftReady ? palette.done : palette.active

  return (
    <div data-testid="zoom-panel-end-prompt-generating" className="py-2">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div
          className={`${scribeText.title} transition-colors duration-500`}
          style={{ color: accent }}
        >
          {draftReady ? 'Progress note ready' : 'Generating Progress Note'}
        </div>
        <div
          className={`${scribeText.meta} font-semibold tabular-nums transition-colors duration-500`}
          style={{ color: accent }}
          data-testid="zoom-panel-end-prompt-progress-pct"
        >
          {pct}%
        </div>
      </div>
      {/* Progress bar: amber fill on a slate track, rounded edges, animated
          width transition so stage jumps glide instead of snapping. */}
      <div
        className={`mb-3 h-[6px] w-full overflow-hidden rounded-full ${palette.track}`}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        data-testid="zoom-panel-end-prompt-progress-bar"
      >
        <div
          className="h-full rounded-full transition-[width,background-color] duration-500 ease-out"
          style={{ width: `${displayed}%`, backgroundColor: accent }}
        />
      </div>
      {/* Active row carries the LIVE stage text, so a regeneration says so. */}
      <ol
        className={`mt-1 space-y-[7px] ${scribeText.meta}`}
        data-testid="zoom-panel-end-prompt-steps"
        aria-live="polite"
      >
        {STEPS.map((step, index) => (
          <StepRow
            key={step.label}
            label={index === activeIndex ? label : step.label}
            state={index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending'}
            note={
              index === activeIndex && regenerating
                ? `Improving the draft — pass ${extraPasses + 2}`
                : undefined
            }
            palette={palette}
          />
        ))}
      </ol>
    </div>
  )
}

export type EndSessionPromptProps = {
  /** True while step 1 (POST /finalize) is in flight. */
  isFinalizing: boolean
  /** True while step 2 (POST /generate-draft) is in flight — shows spinner state. */
  isGeneratingDraft: boolean
  /** Real generation stage/pct streamed from the BE; drives the progress bar (PD-1139). */
  draftProgress?: { stage: string; pct: number } | null
  /** Draft persisted: every step completes and the panel sweeps amber→green. */
  draftReady?: boolean
  /** Fading out: the note is about to be revealed underneath. */
  closing?: boolean
  /**
   * True when the transcript was saved but the AI draft failed — shows the
   * retry state. The transcript is durable, so retry is a pure repeatable read.
   */
  draftError?: boolean
  /** Coral primary action — runs the full Create-Progress-Note finalize chain. */
  onConfirmEnd: () => void
  /** Plain secondary action — closes the prompt and keeps the scribe running. */
  onCancel: () => void
  /** Re-run the AI draft against the saved transcript. Used in the draftError state. */
  onRetryDraft?: () => void
  /** Skip the AI draft and open the Progress Note for manual entry. Used in the draftError state. */
  onWriteManually?: () => void
}

export function EndSessionPrompt({
  isFinalizing,
  isGeneratingDraft,
  draftProgress,
  draftReady,
  closing,
  draftError,
  onConfirmEnd,
  onCancel,
  onRetryDraft,
  onWriteManually,
}: EndSessionPromptProps) {
  const busy = isFinalizing || isGeneratingDraft

  return (
    <div
      data-testid="zoom-panel-end-prompt"
      role="dialog"
      aria-modal="true"
      aria-labelledby="zoom-panel-end-prompt-title"
      className={`absolute inset-0 z-[1000] flex items-center justify-center bg-black/75 p-4 transition-opacity duration-300 ease-out motion-reduce:transition-none ${
        closing ? 'pointer-events-none opacity-0' : 'opacity-100'
      }`}
    >
      <div className="w-full max-w-[290px] rounded-[14px] border border-white/10 bg-[#1e293b] px-5 py-6">
        {isGeneratingDraft ? (
          <>
            <GeneratingProgress progress={draftProgress} draftReady={draftReady} />
            {/* The draft generates server-side and persists even if this tab goes
                away — the call rail offers it back on the visit page (PD-1330).
                Gate on progress frames: they only flow once /generate-draft is
                streaming, i.e. after the call ended. The parent's flag also spans
                the finalize step, where leaving WOULD lose the note. */}
            {!draftReady && draftProgress ? (
              <div
                data-testid="zoom-panel-end-prompt-leave-hint"
                className={`mt-3 text-center ${scribeText.body} leading-relaxed text-slate-500`}
              >
                You can safely leave this page — the note keeps generating in the background and
                will appear as a Draft in the patient's Progress Notes.
              </div>
            ) : null}
          </>
        ) : draftError ? (
          <>
            <div
              id="zoom-panel-end-prompt-title"
              className={`mb-2 text-center ${scribeText.title} text-slate-100`}
            >
              AI draft didn’t generate
            </div>
            <div className={`mb-5 text-center ${scribeText.body} leading-relaxed text-slate-400`}>
              The transcript was saved. You can retry the AI draft, or write the Progress Note
              manually.
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={onRetryDraft}
                data-testid="zoom-panel-end-prompt-retry"
                className={`w-full rounded-lg border-0 p-[10px] ${scribeText.label} text-white`}
                style={{ background: SCRIBE_ACCENT }}
              >
                Retry AI draft
              </button>
              <button
                type="button"
                onClick={onWriteManually}
                data-testid="zoom-panel-end-prompt-write-manually"
                className={`w-full rounded-lg border-0 bg-transparent p-[7px] ${scribeText.label} text-slate-600 hover:text-slate-400`}
              >
                Write note manually
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              id="zoom-panel-end-prompt-title"
              className={`mb-2 text-center ${scribeText.title} text-slate-100`}
            >
              End Session
            </div>
            <div className={`mb-5 text-center ${scribeText.body} leading-relaxed text-slate-400`}>
              End this call? A Progress Note will be created from the transcript captured during the
              session.
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={onConfirmEnd}
                disabled={busy}
                data-testid="zoom-panel-end-prompt-confirm"
                className={`w-full rounded-lg border-0 p-[10px] ${scribeText.label} text-white disabled:cursor-not-allowed disabled:opacity-60`}
                style={{ background: SCRIBE_ACCENT }}
              >
                {isFinalizing ? 'Saving transcript…' : 'End Session'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                data-testid="zoom-panel-end-prompt-cancel"
                className={`w-full rounded-lg border-0 bg-transparent p-[7px] ${scribeText.label} text-slate-600 hover:text-slate-400 disabled:cursor-not-allowed disabled:opacity-60`}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
